import { computed } from 'vue';
import { useStore } from 'vuex';

export function useUserLimits() {
  const store = useStore();

  // Get user limits from store
  const userLimits = computed(() => store.getters.userLimits);

  // Individual limit states as computed properties
  const isAgentLimit = computed(() => userLimits.value.isAgentLimit);
  const isReporterLimit = computed(() => userLimits.value.isReporterLimit);
  const isAdminLimit = computed(() => userLimits.value.isAdminLimit);
  const noAgent = computed(() => userLimits.value.noAgent);
  const noReporter = computed(() => userLimits.value.noReporter);
  const noAdmin = computed(() => userLimits.value.noAdmin);
  const inLimits = computed(() => userLimits.value.inLimits);
  const limitReached = computed(() => userLimits.value.limitReached);

  // Check if a specific user type is available
  const isUserTypeAvailable = (userType) => {
    switch (userType) {
      case 1: // Agent
        return !isAgentLimit.value && !noAgent.value;
      case 2: // Reporter
        return !isReporterLimit.value && !noReporter.value;
      case 3: // Admin
        return !isAdminLimit.value && !noAdmin.value;
      case 5: // Wallboard - no limits
        return true;
      default:
        return false;
    }
  };

  // Check if a specific user type is at limit
  const isUserTypeAtLimit = (userType) => {
    switch (userType) {
      case 1: // Agent
        return isAgentLimit.value;
      case 2: // Reporter
        return isReporterLimit.value;
      case 3: // Admin
        return isAdminLimit.value;
      case 5: // Wallboard - no limits
        return false;
      default:
        return false;
    }
  };

  // Check if a specific user type is disabled
  const isUserTypeDisabled = (userType) => {
    switch (userType) {
      case 1: // Agent
        return noAgent.value;
      case 2: // Reporter
        return noReporter.value;
      case 3: // Admin
        return noAdmin.value;
      case 5: // Wallboard - no limits, never disabled
        return false;
      default:
        return false;
    }
  };

  // Initialize user limits from API response
  const initUserLimits = async () => {
    const resp = await store.dispatch('GetUsersStats');
    if (resp && resp.success) {
      const usersCounts = resp.result.counts;
      const usersLimits = resp.result.limit;
      
      const newLimits = {
        counts: usersCounts,
        limits: usersLimits,
        inLimits: resp.success,
        isAgentLimit: usersCounts.agents >= usersLimits.agents,
        isReporterLimit: usersCounts.reporters >= usersLimits.reporters,
        isAdminLimit: usersCounts.admins >= usersLimits.admins,
        noAgent: usersLimits.agents === 0,
        noReporter: usersLimits.reporters === 0,
        noAdmin: usersLimits.admins === 0,
        limitReached: (usersCounts.agents >= usersLimits.agents && 
                      usersCounts.reporters >= usersLimits.reporters && 
                      usersCounts.admins >= usersLimits.admins),
      };
      
      store.commit('setUserLimits', newLimits);
      return true;
    } else if (resp) {
      store.dispatch('SetMessageDialog', {
        success: false,
        show: true,
        message: resp.message,
      });
    }
    return false;
  };

  // Check if data is already loaded
  const isDataLoaded = computed(() => {
    return store.getters.domainSkills && 
           store.getters.domainRanges && 
           store.getters.domainUsers &&
           userLimits.value.counts.agents !== 0; // This indicates limits have been loaded
  });

  // Load all required data if not already loaded
  const ensureDataLoaded = async () => {
    if (isDataLoaded.value) {
      return true; // Data already loaded
    }

    try {
      // Load domain skills if not loaded
      if (!store.getters.domainSkills) {
        await store.dispatch('GetDomainSkills');
      }
      
      // Load domain ranges if not loaded
      if (!store.getters.domainRanges) {
        await store.dispatch('GetDomainRanges');
      }
      
      // Load domain users if not loaded
      if (!store.getters.domainUsers) {
        await store.dispatch('GetDomainUsers');
      }
      
      // Load user limits if not loaded
      if (userLimits.value.counts.agents === 0) {
        await initUserLimits();
      }
      
      return true;
    } catch (error) {
      console.error('Failed to load required data:', error);
      return false;
    }
  };

  return {
    // State
    userLimits,
    isAgentLimit,
    isReporterLimit,
    isAdminLimit,
    noAgent,
    noReporter,
    noAdmin,
    inLimits,
    limitReached,
    
    // Computed
    isDataLoaded,
    
    // Methods
    initUserLimits,
    ensureDataLoaded,
    isUserTypeAvailable,
    isUserTypeAtLimit,
    isUserTypeDisabled,
  };
} 